<template>
  <v-dialog
    :key="dialog"
    persistent
    v-model="localDialog"
    max-width="900px"
    transition="dialog-bottom-transition">
    <v-card dark style="border: 1px solid #000">
      <v-card-text class="pb-0">
        <v-container>
          <v-form ref="form" lazy-validation>
            <v-row>
              <v-col cols="12" class="pb-0">
                <v-text-field
                  label="Nome"
                  color="yellow"
                  :rules="nameRules"
                  v-model="name"></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0">
                <v-text-field
                  color="yellow"
                  label="E-mail"
                  :rules="emailRules"
                  v-model="email"></v-text-field>
              </v-col>

              <v-col cols="12" class="pb-4 pt-4">
                <p class="text-h6">Permissões</p>
                <role-tree v-model="roles" :all-roles="allRoles" />
              </v-col>

              <v-col cols="12" class="pb-0 pt-0">
                <v-autocomplete
                  chips
                  multiple
                  color="yellow"
                  item-value="id"
                  item-text="name"
                  label="Perfis"
                  :items="allProfiles"
                  v-model="profiles"
                  v-show="!createForm">
                  <template v-slot:item="{ item, on, attrs }">
                    <v-list-item v-on="on" dark>
                      <v-list-item-action>
                        <v-simple-checkbox
                          color="yellow"
                          :value="attrs.inputValue"
                          v-on="on"
                          :ripple="false"></v-simple-checkbox>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title :class="attrs.inputValue ? 'primary--text' : ''">
                          {{ item.name }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ attrs, item, selected }">
                    <v-chip
                      close
                      v-bind="attrs"
                      :input-value="selected"
                      @click:close="remove(item.id, 'profile')">
                      {{ item.name }}
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" class="pb-0">
                <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      readonly
                      clearable
                      color="yellow"
                      prepend-icon="mdi-calendar"
                      label="Data para expirar senha"
                      v-on="on"
                      v-bind="attrs"
                      v-model="dateFormatted"
                      @click:clear="password_due_date = ''"
                      @blur="dateFormatted = parseDate(dateFormatted)"></v-text-field>
                  </template>
                  <v-date-picker
                    dark
                    locale="pt-br"
                    color="yellow"
                    :min="minDate"
                    header-color="dark"
                    v-model="password_due_date"></v-date-picker>
                </v-menu>
                <v-alert text dense type="info" class="ma-0" border="left" v-if="!id">
                  Por padrão a data para expirar a senha é definida para
                  {{ PASSWORD_EXPIRATION_DAYS }} dias!
                </v-alert>
              </v-col>
              <v-col cols="12" md="3" class="pb-0">
                <v-switch inset hide-details color="yellow" v-model="twoFA" label="2FA"></v-switch>
              </v-col>
              <v-col cols="12" md="3" class="pb-0">
                <v-switch
                  inset
                  hide-details
                  color="yellow"
                  v-model="force_login"
                  label="Forçar Login"></v-switch>
              </v-col>
              <v-col cols="12" md="2" class="pb-0">
                <v-switch
                  inset
                  hide-details
                  color="yellow"
                  v-model="active"
                  label="Ativo"></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="handleClose"> Fechar </v-btn>
        <v-btn color="yellow" text :loading="loading" @click="save()"> Salvar </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import { createUser, updateUser } from "@/services/user";
import { IRole, IUsers } from "@/utils/interfaces";
import RoleTree from "@/components/RoleTree.vue";

export default defineComponent({
  name: "UserDialog",
  components: {
    RoleTree,
  },
  props: {
    user: { type: Object as PropType<IUsers>, default: () => ({}) },
    allRoles: { type: Array as PropType<IRole[]>, default: () => [] },
    allProfiles: { type: Array, default: () => [] },
    createForm: { type: Boolean, default: false },
    minDate: { type: String, default: "" },
    dialog: Boolean,
  },
  data() {
    return {
      PASSWORD_EXPIRATION_DAYS: 60,
      id: "",
      name: "",
      email: "",
      twoFA: false,
      active: false,
      inactive_reason: null as string | null,
      force_login: false,
      password_due_date: "",
      dateFormatted: "",
      roles: [] as IRole[],
      profiles: [] as any[],
      menu: false,
      loading: false,
      emailRules: [
        (v: any): any => !!v || "E-mail deve ser informado",
        (v: any): any => /.+@.+/.test(v) || "E-mail inválido | ex: pessoa@domínio.com",
      ],
      nameRules: [(v: any) => !!v || "O nome é obrigatório"],
    };
  },
  computed: {
    localDialog: {
      get(): boolean {
        return this.dialog;
      },
      set(val: boolean) {
        if (!val) this.$emit("close");
      },
    },
  },

  watch: {
    user: {
      immediate: true,
      handler(newUser) {
        if (newUser && Object.keys(newUser).length) {
          this.id = newUser.id || "";
          this.name = newUser.name || "";
          this.email = newUser.email || "";
          this.active = newUser.active || false;
          this.force_login = newUser.force_login || false;
          this.twoFA = ["TOTP", "SMS", "EMAIL"].includes(newUser._2fa);
          this.password_due_date = newUser.password_due_date
            ? newUser.password_due_date.split("T")[0]
            : "";
          this.roles = [...(newUser.roles || [])];
          this.profiles = [...(newUser.profiles || [])];
          this.inactive_reason = null;
        }
      },
    },
    dialog: {
      immediate: true,
      handler(value) {
        if (!value) {
          this.handleClose();
        }
      },
    },
  },
  methods: {
    handleClose() {
      this.id = "";
      this.name = "";
      this.email = "";
      this.active = false;
      this.force_login = false;
      this.twoFA = false;
      this.password_due_date = "";
      this.roles = [];
      this.profiles = [];
      this.inactive_reason = null;
      if (this.$refs.form) {
        (this.$refs.form as any).reset();
      }
      this.$emit("close");
    },

    parseDate(date: any): string {
      if (!date) return "";
      const [day, month, year] = date.split("/");
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    },

    async save() {
      if (!(this.$refs.form as any).validate()) return;

      const payload: any = {
        id: this.id,
        name: this.name,
        email: this.email,
        _2fa: this.twoFA === true ? "TOTP" : null,
        active: this.active,
        force_login: this.force_login,
        password_due_date:
          `${this.parseDate(this.dateFormatted)}T00:00:00.000Z` == "T00:00:00.000Z"
            ? null
            : `${this.parseDate(this.dateFormatted)}T00:00:00.000Z`,
        roles: this.roles.map((role: any) => (role.id ? role.id : role)),
        profiles: this.profiles.map((profile: any) => (profile.id ? profile.id : profile)),
        inactive_reason: !this.active ? this.inactive_reason : null,
      };

      if (!payload._2fa) payload.totp_key = null;

      this.loading = true;

      try {
        if (this.createForm) {
          await createUser(payload);
          this.$emit("snackbar", { message: "Usuário foi criado!" });
          this.$emit("saved", payload);
          this.handleClose();
        } else {
          const res: any = await updateUser(this.id, payload);
          this.name = res.data.name;
          this.email = res.data.email;
          this.active = res.data.active;
          this.force_login = res.data.force_login;
          this.twoFA = ["TOTP", "SMS", "EMAIL"].includes(res.data._2fa);
          this.password_due_date = res.data.password_due_date.split(" ")[0];
          this.roles = res.data.roles.map((role: any) => role);
          this.profiles = res.data.profiles.map((profile: any) => profile);
          this.$emit("snackbar", { message: "Usuário foi atualizado!" });
        }
        this.$emit("saved", payload);
        this.handleClose();
      } catch (err) {
        console.error(err);
        this.$emit("snackbar", {
          message: this.createForm ? "Erro ao criar usuário!" : "Erro ao atualizar usuário!",
        });
      } finally {
        this.loading = false;
      }
    },

    remove(id: any, type: any): void {
      if (type === "role") {
        const index = this.roles.findIndex((role: IRole) => role.id === id);
        if (index !== -1) {
          this.roles.splice(index, 1);
        }
      } else if (type === "profile") {
        const index = this.profiles.findIndex((profile: any) => profile.id === id);
        if (index !== -1) {
          this.profiles.splice(index, 1);
        }
      }
    },
  },
});
</script>
<style lang="scss" scoped>
.v-list {
  background: #1e1e1e !important;
}
</style>
